import React, { useState, useRef, useEffect } from 'react';
import SaveButtonWithModal from "./SaveButtonWithModel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";

const person = [
  "Male", "Female", "Youth ","Physical ",
];

// Validation helpers
const isValidName = (name) => {
  if (!name) return false;
  const words = name.trim().split(/\s+/);
  return words.length <= 8 && /^[A-Za-z\s]+$/.test(name);
};
const isValidSurname = (surname) => {
  if (!surname) return false;
  const words = surname.trim().split(/\s+/);
  return words.length <= 8 && /^[A-Za-z\s]+$/.test(surname);
};
// Accept Date object or string in YYYY-MM-DD
const isValidDateOfBirth = (dob) => {
  if (!dob) return false;
  if (dob instanceof Date && !isNaN(dob)) return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(dob);
};
const isValidId = (id) => {
  return id && /^[A-Za-z0-9]+$/.test(id) && id.length >= 5;
};
const isValidMobile = (mobile) => {
  return /^\+?[0-9\s\-]{7,20}$/.test(mobile);
};
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};
const isValidPassword = (password) => {
  return /^(?=.*[A-Za-z])(?=.*\d).{6,}$/.test(password);
};
const isValidWhatsapp = (whatsapp) => {
  return /^\+?[0-9\s\-]{7,20}$/.test(whatsapp);
};
const isValidPhysicalAddress = (address) => {
  return address && address.length >= 5 && address.length <= 100;
};
const isValidPostalCode = (code) => {
  return /^\d{4,6}$/.test(code);
};
const isValidOrganizationName = (name) => {
  return name && name.length >= 2 && name.length <= 100;
};

const isValidOrganizationType = (type) => {
  return type && type.trim() !== "";
};

const isValidRegistrationDocument = (doc) => {
  return doc && doc.length >= 2 && doc.length <= 100;
};

const isValidRegistrationNumber = (number) => {
  return number && /^[A-Za-z0-9\-]+$/.test(number) && number.length >= 3;
};
const handleSelect = (option, formData, onFormDataChange) => {
  onFormDataChange({ ...formData, typeOfId: option });
};
const requiredFields = [
  "organizationName", "organizationType", "registrationDocument", "registrationNumber",
  "mobile", "email", "password", "whatsapp", "physicalAddress", "postalAddress"
];
const fieldValidators = {
    cooperativeName: isValidOrganizationName, // Add this line
  organizationName: isValidOrganizationName,
  organizationType: isValidOrganizationType,
  registrationDocument: isValidRegistrationDocument,
  registrationNumber: isValidRegistrationNumber,
  mobile: isValidMobile,
  email: isValidEmail,
  password: isValidPassword,
  whatsapp: isValidWhatsapp,
  physicalAddress: isValidPhysicalAddress,
  postalAddress: isValidPhysicalAddress, // Reuse the same validator
};

const fieldErrorMessages = {
  cooperativeName: "Organization name must be 2-100 characters.", // Add this line
  organizationName: "Organization name must be 2-100 characters.",
  organizationType: "Please select an organization type.",
  registrationDocument: "Registration document name must be 2-100 characters.",
  registrationNumber: "Registration number is required.",
  mobile: "Enter a valid phone number (may start with +).",
  whatsapp: "Enter a valid WhatsApp number (may start with +).",
  email: "Invalid email address.",
  password: "Min 6 chars, 1 letter & 1 number.",
  physicalAddress: "5-100 characters.",
  postalAddress: "5-100 characters.",
};

const CustomDateDiv = React.forwardRef(({ value, onClick }, ref) => (
  <div
    onClick={onClick}
    ref={ref}
    className="xl:w-[390px] lg:w-[330px] md:w-[250px] w-[115px] h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] text-[#979797] border rounded-l-[5px] bg-[#E7EBEB] border-[#E7EBEB] cursor-pointer font-bold"
  >
    {value || "YYYY-MM-DD"}
  </div>
));

const UnselectedIcon = () => (
  <svg
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
  >
    <circle cx="8.5" cy="8.5" r="8" fill="#D9D9D9" stroke="none" />
  </svg>
);

const SelectedIcon = () => (
  <svg
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-[6px] h-[6px] md:w-[17px] md:h-[17px]"
  >
    <circle cx="8.5" cy="8.5" r="8" fill="#25242C" stroke="#FFBD26" />
    <path
      d="M5 8.5L7.5 11L12 6"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const OrganizationForm0 = ({
  formData,
  onFormDataChange,
  race,
  onRaceChange,
  gender: selectedGender,
  onGenderChange,
  disability,
  onDisabilityChange,
  onSave,
  orgFormData, // Add this prop to receive organization-specific data
  onOrgFormDataChange // Add this to update organization data
}) => {
  const [showModal, setShowModal] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const currentData = orgFormData || formData;
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Organization type options
  const organizationTypes = [
    "Association (ASS)", 
    "Cooperative (COO)", 
    "Community (COM)", 
    "Contact (CON)", 
    "Development Partner (DEP)", 
    "Education (EDU)", 
    "Inputs Supplier (IPS)", 
    "Institution (INS)", 
    "Investor (INV)", 
    "Offtake Partner (OFP)", 
    "Service Provider (SER)"
  ];

  // Validate all fields on change and on submit
  const validateAllFields = (data = formData) => {
    let errors = {};
    requiredFields.forEach((field) => {
      const value = data[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        errors[field] = "This field is required.";
      } else if (fieldValidators[field] && !fieldValidators[field](value)) {
        errors[field] = fieldErrorMessages[field];
      }
    });
    return errors;
  };
  const organizationData = orgFormData || {};
  // Real-time validation per field
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (onOrgFormDataChange && orgFormData) {
      onOrgFormDataChange({ ...orgFormData, [name]: value });
    } else {
      // Fallback to personal form data
      onFormDataChange({ ...formData, [name]: value });
    }

    // Update both formData and orgFormData to keep them in sync
    onFormDataChange({ ...formData, [name]: value });
    if (onOrgFormDataChange) {
      onOrgFormDataChange({ ...orgFormData, [name]: value });
    }

    let error = "";
    if (!value || value.trim() === "") {
      error = "This field is required.";
    } else if (fieldValidators[name] && !fieldValidators[name](value)) {
      error = fieldErrorMessages[name];
    }
    
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };


  // Date picker change handler
  const handleDateChange = (date) => {
    onFormDataChange({ ...formData, dateOfBirth: date });
    let error = "";
    if (!date) {
      error = "This field is required.";
    } else if (!isValidDateOfBirth(date)) {
      error = fieldErrorMessages.dateOfBirth;
    }
    setFieldErrors((prev) => ({ ...prev, dateOfBirth: error }));
  };

  // Check for empty gender/disability
  const handleGenderChange = (val) => {
    onGenderChange(val);
    if (!val) {
      setFieldErrors((prev) => ({ ...prev, gender: "Please select gender." }));
    } else {
      setFieldErrors((prev) => {
        const { gender, ...rest } = prev;
        return rest;
      });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOrgDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDisabilityChange = (val) => {
    onDisabilityChange(val);
    if (!val) {
      setFieldErrors((prev) => ({ ...prev, disability: "Please select disability/ability." }));
    } else {
      setFieldErrors((prev) => {
        const { disability, ...rest } = prev;
        return rest;
      });
    }
  };

  // On Save: validate all
  const handleSave = () => {
    const errors = validateAllFields();
    if (!selectedGender) errors.gender = "Please select gender.";
    if (!disability) errors.disability = "Please select disability/ability.";
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSubmitError("Please fix the errors below.");
      return;
    }
    setSubmitError("");
    setShowModal(true);
    onSave({
      ...formData,
      race,
      gender: selectedGender,
      disability,
    });
  };

  return (
    <div className="mx-auto md:mt-3 mt-1">
      <div className="flex-grow ">
        <h2 className="font-bold text-[10px] text-center  underline md:text-[36px] text-black">
          Section 1 : Organization information {/* Changed title */}
        </h2>
        <p className="text-[8px] md:text-[24px] italic text-black md:mt-2 md:mb-4 mb-2">
          Please enter your organization information in the section below. {/* Changed description */}
        </p>
      </div>
      
      {submitError && <div className="text-red-600 text-center mb-1 text-sm">{submitError}</div>}
      
      <form onSubmit={e => e.preventDefault()}>
        <div>
          <div className="flex flex-col md:gap-y-3 gap-1 w-full">
            <div className="flex md:flex-row md:gap-x-2 gap-x-1">
              {/* Organization Name */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Name of Organization</label>
                <input
                  name="cooperativeName" // Changed to match orgFormData field
                  value={orgFormData?.cooperativeName || formData.cooperativeName || ""}
                  onChange={handleChange}
                  placeholder="Organization Name"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.organizationName && <div className="text-red-500 text-xs mt-1">{fieldErrors.organizationName}</div>}
              </div>

              {/* Type of Organization */}
              <div className="w-full relative" ref={dropdownRef}>
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black">
                  <span className="text-red-500">*</span>Type of Organization
                </label>
                <div 
                  onClick={() => setIsOrgDropdownOpen(!isOrgDropdownOpen)}
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] flex justify-between items-center cursor-pointer"
                >
                  <span className={formData.organizationType ? "text-black" : "text-[#979797]"}>
                    {formData.organizationType || "Select Organization Type"}
                  </span>
                  <motion.svg
                    animate={{ rotate: isOrgDropdownOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="w-4 h-4 md:w-6 md:h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </motion.svg>
                </div>
                
                <AnimatePresence>
                  {isOrgDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 w-full border border-[#979797]/80 bg-[#152C24] rounded shadow-lg max-h-[300px] overflow-y-auto z-[1000] mt-1"
                    >
                      <ul className="py-2">
                        {organizationTypes.map((item, index) => (
                          <li
                            key={index}
                            onClick={() => {
                              onFormDataChange({ ...formData, organizationType: item });
                              setIsOrgDropdownOpen(false);
                            }}
                            className="flex justify-between items-center px-4 py-2 hover:bg-gray-700 cursor-pointer"
                          >
                            <span className="text-white text-sm flex-1 mr-2">
                              {item}
                            </span>
                            <div className="flex items-center">
                              {formData.organizationType === item ? (
                                <SelectedIcon />
                              ) : (
                                <UnselectedIcon />
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  )}
                </AnimatePresence>
                {fieldErrors.organizationType && <div className="text-red-500 text-xs mt-1">{fieldErrors.organizationType}</div>}
              </div>
            </div>

            <div className="flex md:flex-row md:gap-x-2 gap-x-1">
              {/* Registration Document */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Registration Document</label>
                <input
                  name="registrationDocument"
                  value={formData.registrationDocument || ""}
                  onChange={handleChange}
                  placeholder="e.g., CIPC Certificate, Registration Number"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.registrationDocument && <div className="text-red-500 text-xs mt-1">{fieldErrors.registrationDocument}</div>}
              </div>

              {/* Registration Number */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Number on Document</label>
                <input
                  name="registrationNumber"
                  value={formData.registrationNumber || ""}
                  onChange={handleChange}
                  placeholder="Registration Number"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.registrationNumber && <div className="text-red-500 text-xs mt-1">{fieldErrors.registrationNumber}</div>}
              </div>
            </div>

            {/* Contact Information Row */}
            <div className="flex md:flex-row md:gap-x-2 gap-x-1">
              {/* Mobile Number */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Mobile Number</label>
                <input
                  name="mobile"
                  value={formData.mobile || ""}
                  onChange={handleChange}
                  required
                  placeholder="0XXXXXXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-0 border-b-[3px] border-[#979797] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.mobile && <div className="text-red-500 text-xs mt-1">{fieldErrors.mobile}</div>}
              </div>

              {/* WhatsApp Number */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>WhatsApp Number</label>
                <input
                  name="whatsapp"
                  value={formData.whatsapp || ""}
                  onChange={handleChange}
                  required
                  placeholder="0XXXXXXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.whatsapp && <div className="text-red-500 text-xs mt-1">{fieldErrors.whatsapp}</div>}
              </div>
            </div>

            {/* Email and Password Row */}
            <div className="flex md:flex-row md:gap-x-2 gap-x-1">
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Email</label>
                <input
                  name="email"
                  value={formData.email || ""}
                  onChange={handleChange}
                  placeholder="organization@email.com"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.email && <div className="text-red-500 text-xs mt-1">{fieldErrors.email}</div>}
              </div>
              
              <div className="w-full">
                <div className="flex justify-between items-center">
                  <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Password</label>
                  <p className='md:text-[16px] text-[4px] font-normal italic'>Set a password to secure your organization profile</p>
                </div>
                <div className="relative">
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password || ""}
                    onChange={handleChange}
                    required
                    placeholder="Password"
                    className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-0 border-b-[3px] border-[#979797] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-2 md:text-[30px] text-[8px] top-1/2 transform -translate-y-1/2 text-[#979797]"
                  >
                    {showPassword ? <FiEye /> : <FiEyeOff />}
                  </button>
                </div>
                {fieldErrors.password && <div className="text-red-500 text-xs mt-1">{fieldErrors.password}</div>}
              </div>
            </div>

            {/* Address Row */}
            <div className="flex md:flex-row md:gap-x-2 gap-x-1">
              {/* Physical Address */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Physical Address</label>
                <input
                  name="physicalAddress"
                  value={formData.physicalAddress || ""}
                  onChange={handleChange}
                  required
                  placeholder="Organization Physical Address"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.physicalAddress && <div className="text-red-500 text-xs mt-1">{fieldErrors.physicalAddress}</div>}
              </div>
              
              {/* Postal Address */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[24px] px-1 text-black"><span className="text-red-500">*</span>Postal Address</label>
                <input
                  name="postalAddress"
                  value={formData.postalAddress || ""}
                  onChange={handleChange}
                  required
                  placeholder="Organization Postal Address"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-2 md:pl-3 text-[8px] md:text-[24px] font-bold border-[#979797] border-b-[3px] md:rounded-[10px] rounded-[4px] text-black bg-[#F5F5F5] placeholder:text-[#979797] placeholder:font-bold md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[24px]"
                />
                {fieldErrors.postalAddress && <div className="text-red-500 text-xs mt-1">{fieldErrors.postalAddress}</div>}
              </div>
            </div>
          </div>
        </div>
      </form>
      
      {/* Save button */}
      
      {/* Save Modal */}
      {showModal && (
        <SaveButtonWithModal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default OrganizationForm0;